import type { FirebaseApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { TakiConfig } from '@taki/runtime-config';
import { initFirebase } from './firebase.js';
import { type ContactsRepository, createContactsRepository } from './contacts.js';
import { type AssetsRepository, createAssetsRepository } from './assets.js';
import { type LoanRepository, createLoanRepository } from './loans.js';
import { type WarrantyRepository, createWarrantyRepository } from './warranties.js';
import { type IncidentRepository, createIncidentRepository } from './incidents.js';
import { type MeasurementRepository, createMeasurementRepository } from './measurements.js';
import { type FormRepository, createFormRepository } from './forms.js';
import { type CampaignRepository, createCampaignRepository } from './campaigns.js';
import { type AccessGrantRepository, createAccessGrantRepository } from './accessGrants.js';
import { type PolicyRepository, createPolicyRepository } from './policies.js';
import { type RegisterSessionRepository, createRegisterSessionRepository } from './registerSessions.js';
import { type RelationshipRepository, createRelationshipRepository } from './relationships.js';
import { type GroupRepository, createGroupRepository } from './groups.js';
import { type PlaceRepository, createPlaceRepository } from './places.js';
import { type PriceListRepository, createPriceListRepository } from './priceLists.js';
import { type StockRepository, createStockRepository } from './stocks.js';
import { type StockMovementRepository, createStockMovementRepository } from './stockMovements.js';
import { type AvailabilityRepository, createAvailabilityRepository } from './availabilities.js';
import { type ShiftRepository, createShiftRepository } from './shifts.js';
import { type AbsenceRepository, createAbsenceRepository } from './absences.js';
import { type AttendanceRepository, createAttendanceRepository } from './attendances.js';
import { type SubscriptionRepository, createSubscriptionRepository } from './subscriptions.js';
import { type AgreementRepository, createAgreementRepository } from './agreements.js';
import { type DonationRepository, createDonationRepository } from './donations.js';
import { type ExpenseRepository, createExpenseRepository } from './expenses.js';
import { type ShipmentRepository, createShipmentRepository } from './shipments.js';
import { type CustodyRepository, createCustodyRepository } from './custodies.js';
import { type TicketRepository, createTicketRepository } from './tickets.js';
import { type InspectionRepository, createInspectionRepository } from './inspections.js';
import { type MessageRepository, createMessageRepository } from './messages.js';
import { type ReviewRepository, createReviewRepository } from './reviews.js';
import { type ConsentRepository, createConsentRepository } from './consents.js';
import { type VoucherRepository, createVoucherRepository } from './vouchers.js';
import { type CreditRepository, createCreditRepository } from './credits.js';
import { type CompositionRepository, createCompositionRepository } from './compositions.js';
import { type LotRepository, createLotRepository } from './lots.js';
import { type RouteRepository, createRouteRepository } from './routes.js';
import { type WorkerRepository, createWorkerRepository } from './workers.js';
import { type ProductRepository, createProductRepository } from './products.js';
import { type ResourceRepository, createResourceRepository } from './resources.js';
import { type BookingRepository, createBookingRepository } from './bookings.js';
import { type EventRepository, createEventRepository } from './events.js';
import { type RegistrationRepository, createRegistrationRepository } from './registrations.js';
import { type OrderRepository, createOrderRepository } from './orders.js';
import { type InvoiceRepository, createInvoiceRepository } from './invoices.js';
import { type PaymentRepository, createPaymentRepository } from './payments.js';
import { type WorkOrderRepository, createWorkOrderRepository } from './workOrders.js';
import { type TaskRepository, createTaskRepository } from './tasks.js';
import { type TimesheetRepository, createTimesheetRepository } from './timesheets.js';
import { type DocumentRepository, createDocumentRepository } from './documents.js';
import { type ProjectRepository, createProjectRepository } from './projects.js';
import { type ListingRepository, createListingRepository } from './listings.js';
import { type OpportunityRepository, createOpportunityRepository } from './opportunities.js';
import { type CareRecordRepository, createCareRecordRepository } from './careRecords.js';
import { type CredentialRepository, createCredentialRepository } from './credentials.js';

/**
 * Instance du SDK Taki, point d'accès UNIQUE aux données pour une app.
 * Aucune app ne doit importer firebase/firestore directement (cage IA).
 */
export interface TakiSdk {
  app: FirebaseApp;
  db: Firestore;
  contacts: ContactsRepository;
  assets: AssetsRepository;
  loans: LoanRepository;
  warranties: WarrantyRepository;
  incidents: IncidentRepository;
  measurements: MeasurementRepository;
  forms: FormRepository;
  campaigns: CampaignRepository;
  accessGrants: AccessGrantRepository;
  policies: PolicyRepository;
  registerSessions: RegisterSessionRepository;
  relationships: RelationshipRepository;
  groups: GroupRepository;
  places: PlaceRepository;
  priceLists: PriceListRepository;
  stocks: StockRepository;
  stockMovements: StockMovementRepository;
  availabilities: AvailabilityRepository;
  shifts: ShiftRepository;
  absences: AbsenceRepository;
  attendances: AttendanceRepository;
  subscriptions: SubscriptionRepository;
  agreements: AgreementRepository;
  donations: DonationRepository;
  expenses: ExpenseRepository;
  shipments: ShipmentRepository;
  custodies: CustodyRepository;
  tickets: TicketRepository;
  inspections: InspectionRepository;
  messages: MessageRepository;
  reviews: ReviewRepository;
  consents: ConsentRepository;
  vouchers: VoucherRepository;
  credits: CreditRepository;
  compositions: CompositionRepository;
  lots: LotRepository;
  routes: RouteRepository;
  workers: WorkerRepository;
  products: ProductRepository;
  resources: ResourceRepository;
  bookings: BookingRepository;
  events: EventRepository;
  registrations: RegistrationRepository;
  orders: OrderRepository;
  invoices: InvoiceRepository;
  payments: PaymentRepository;
  workOrders: WorkOrderRepository;
  tasks: TaskRepository;
  timesheets: TimesheetRepository;
  documents: DocumentRepository;
  projects: ProjectRepository;
  listings: ListingRepository;
  opportunities: OpportunityRepository;
  careRecords: CareRecordRepository;
  credentials: CredentialRepository;
}

/**
 * Construit le SDK à partir de la config chargée au runtime :
 * initialise Firebase, ouvre Firestore et expose les repositories typés.
 *
 * Si la config porte un bloc `emulators`, Firestore est branché sur l'émulateur
 * local plutôt que sur le cloud (cf. `initFirebase` et `pnpm dev:local`).
 */
export function createTakiSdk(cfg: TakiConfig): TakiSdk {
  const { app, db } = initFirebase(cfg);
  const actorId = () => getAuth(app).currentUser?.uid ?? 'system';
  return {
    app,
    db,
    contacts: createContactsRepository(db, actorId, cfg.orgId),
    assets: createAssetsRepository(db, actorId, cfg.orgId),
    loans: createLoanRepository(db, actorId, cfg.orgId),
    warranties: createWarrantyRepository(db, actorId, cfg.orgId),
    incidents: createIncidentRepository(db, actorId, cfg.orgId),
    measurements: createMeasurementRepository(db, actorId, cfg.orgId),
    forms: createFormRepository(db, actorId, cfg.orgId),
    campaigns: createCampaignRepository(db, actorId, cfg.orgId),
    accessGrants: createAccessGrantRepository(db, actorId, cfg.orgId),
    policies: createPolicyRepository(db, actorId, cfg.orgId),
    registerSessions: createRegisterSessionRepository(db, actorId, cfg.orgId),
    relationships: createRelationshipRepository(db, actorId, cfg.orgId),
    groups: createGroupRepository(db, actorId, cfg.orgId),
    places: createPlaceRepository(db, actorId, cfg.orgId),
    priceLists: createPriceListRepository(db, actorId, cfg.orgId),
    stocks: createStockRepository(db, actorId, cfg.orgId),
    stockMovements: createStockMovementRepository(db, actorId, cfg.orgId),
    availabilities: createAvailabilityRepository(db, actorId, cfg.orgId),
    shifts: createShiftRepository(db, actorId, cfg.orgId),
    absences: createAbsenceRepository(db, actorId, cfg.orgId),
    attendances: createAttendanceRepository(db, actorId, cfg.orgId),
    subscriptions: createSubscriptionRepository(db, actorId, cfg.orgId),
    agreements: createAgreementRepository(db, actorId, cfg.orgId),
    donations: createDonationRepository(db, actorId, cfg.orgId),
    expenses: createExpenseRepository(db, actorId, cfg.orgId),
    shipments: createShipmentRepository(db, actorId, cfg.orgId),
    custodies: createCustodyRepository(db, actorId, cfg.orgId),
    tickets: createTicketRepository(db, actorId, cfg.orgId),
    inspections: createInspectionRepository(db, actorId, cfg.orgId),
    messages: createMessageRepository(db, actorId, cfg.orgId),
    reviews: createReviewRepository(db, actorId, cfg.orgId),
    consents: createConsentRepository(db, actorId, cfg.orgId),
    vouchers: createVoucherRepository(db, actorId, cfg.orgId),
    credits: createCreditRepository(db, actorId, cfg.orgId),
    compositions: createCompositionRepository(db, actorId, cfg.orgId),
    lots: createLotRepository(db, actorId, cfg.orgId),
    routes: createRouteRepository(db, actorId, cfg.orgId),
    workers: createWorkerRepository(db, actorId, cfg.orgId),
    products: createProductRepository(db, actorId, cfg.orgId),
    resources: createResourceRepository(db, actorId, cfg.orgId),
    bookings: createBookingRepository(db, actorId, cfg.orgId),
    events: createEventRepository(db, actorId, cfg.orgId),
    registrations: createRegistrationRepository(db, actorId, cfg.orgId),
    orders: createOrderRepository(db, actorId, cfg.orgId),
    invoices: createInvoiceRepository(db, actorId, cfg.orgId),
    payments: createPaymentRepository(db, actorId, cfg.orgId),
    workOrders: createWorkOrderRepository(db, actorId, cfg.orgId),
    tasks: createTaskRepository(db, actorId, cfg.orgId),
    timesheets: createTimesheetRepository(db, actorId, cfg.orgId),
    documents: createDocumentRepository(db, actorId, cfg.orgId),
    projects: createProjectRepository(db, actorId, cfg.orgId),
    listings: createListingRepository(db, actorId, cfg.orgId),
    opportunities: createOpportunityRepository(db, actorId, cfg.orgId),
    careRecords: createCareRecordRepository(db, actorId, cfg.orgId),
    credentials: createCredentialRepository(db, actorId, cfg.orgId),
  };
}
